import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";

// Package up colors for convenient use
const COLORS = {
  navy: "#0f1729",
  navyLight: "#1a2540",
  accent: "#3b82f6",
  accentDark: "#2563eb",
  purple: "#8b5cf6",
  bg: "#f1f5f9",
  warning: "#fbbf24",
  red: "#ef4444",
  green: "#22c55e",
  kpi: [
    "#3b82f6",
    "#8b5cf6",
    "#10b981",
    "#fbbf24",
    "#ef4444",
    "#6366f1",
    "#818cf8",
  ],
};

/**
 * CsvUpload – CSV/Excel upload & analysis component
 * Handles drag/drop upload, parses, analyzes, and renders results.
 */
export default function CsvUpload() {
  // ------------------ State
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // ------------------ File handling functions
  function inspectFiles(fileList) {
    let error = "";
    if (fileList.length === 0) return { ok: false, error: "No file selected" };
    if (fileList.length > 8)
      return { ok: false, error: "No more than 8 files supported" };

    for (let file of fileList) {
      if (
        !(
          file.type === "text/csv" ||
          file.name.endsWith(".csv") ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.type === "application/vnd.ms-excel" ||
          file.name.endsWith(".xlsx") ||
          file.name.endsWith(".xls")
        )
      ) {
        error = `Unsupported file: ${file.name}`;
      }
      if (file.size > 20 * 1024 * 1024) {
        error = `File ${file.name} is too large (max 20 MB)`;
      }
    }
    return { ok: error === "", error: error };
  }

  function handleFiles(fileList) {
    const incoming = Array.from(fileList);
    setFiles((prev) => {
      // accumulate across multiple drops/selections, dedupe by name+size
      const map = new Map(prev.map((f) => [f.name + f.size, f]));
      incoming.forEach((f) => map.set(f.name + f.size, f));
      const combined = Array.from(map.values());
      const inspect = inspectFiles(combined);
      if (!inspect.ok) {
        setFileError(inspect.error);
        return prev;          // keep what we had
      }
      setFileError("");
      return combined;
    });
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files) {
      handleFiles(e.dataTransfer.files);
    }
  }

  function handleFileInput(e) {
    if (e.target.files) handleFiles(e.target.files);
    e.target.value = "";   // reset so the same file can be re-picked / more added
  }

  function removeFile(idx) {
    const newFiles = files.slice();
    newFiles.splice(idx, 1);
    setFiles(newFiles);
  }

  // Used for warning about too long date ranges (could throttle processing)
  function isRangeTooLong(from, to) {
    if (!from || !to) return false;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    return (toDate - fromDate) / (1000 * 3600 * 24) > 180;
  }

  // ------------------ Analysis (REAL backend /analyze)
  async function handleAnalyze() {
    if (files.length === 0) {
      setFileError("Select a file before analyzing.");
      return;
    }
    setAnalyzing(true);
    setFileError("");
    setAnalysisResult(null);
    try {
      const API = process.env.REACT_APP_API_URL || "http://localhost:8002";
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));   // 1..N files
      if (dateRange.from) fd.append("start_date", dateRange.from);
      if (dateRange.to) fd.append("end_date", dateRange.to);

      const resp = await fetch(`${API}/analyze`, {
        method: "POST",
        headers: { Authorization: "Bearer demo-key-123" },
        body: fd,
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || "Analysis failed");

      // ---- transform the real result into the display shape ----
      const km = d.key_metrics || {};
      const primary = km.primary_measure;
      const kpis = [{ label: "Rows", value: km.total_rows ?? d.rows ?? 0 }];
      if (primary) {
        kpis.push(
          { label: `Total ${primary}`, value: km.total ?? 0 },
          { label: `Average ${primary}`, value: Math.round(km.average ?? 0) },
          { label: `Highest ${primary}`, value: km.highest ?? 0 },
          { label: `Lowest ${primary}`, value: km.lowest ?? 0 },
        );
      }
      kpis.push({ label: "Columns", value: d.columns ?? 0 });

      const charts = Array.isArray(d.charts) ? d.charts : [];
      const bar = charts.find((c) => c.type === "bar");
      const line = charts.find((c) => c.type === "line");
      const barData = bar
        ? bar.data.map((r) => ({ name: String(r[bar.x_key]), value: Number(r[bar.y_key]) || 0 }))
        : null;
      const lineData = line
        ? line.data.map((r) => ({ date: String(r[line.x_key]), count: Number(r[line.y_key]) || 0 }))
        : null;

      const risksRaw = Array.isArray(d.risk_alerts) ? d.risk_alerts : [];
      const risks = risksRaw.map((a) => ({
        type: a.type || a.title || "Risk",
        severity: a.severity || "low",
        description: a.description || a.message || "",
        count: a.count ?? a.affected ?? 0,
      }));

      // detailed_insights is a dict ({dataset_overview, top_record, ...}); render defensively
      const asText = (v) =>
        v == null ? "" : typeof v === "string" ? v
          : typeof v === "object"
            ? Object.entries(v).map(([k, val]) => `${k.replace(/_/g, " ")}: ${typeof val === "object" ? JSON.stringify(val) : val}`).join("  •  ")
            : String(v);
      let insights = [];
      const di = d.detailed_insights;
      if (Array.isArray(di)) {
        insights = di.map((i) => ({
          title: i.title || i.label || "Insight",
          description: i.description || i.text || asText(i),
        }));
      } else if (di && typeof di === "object") {
        const TITLES = {
          dataset_overview: "Dataset Overview", top_record: "Top Record",
          bottom_record: "Bottom Record", group_breakdowns: "Group Breakdowns",
        };
        insights = Object.keys(di)
          .filter((k) => di[k] != null && (typeof di[k] !== "object" || Object.keys(di[k]).length))
          .map((k) => ({ title: TITLES[k] || k.replace(/_/g, " "), description: asText(di[k]) }));
      }
      if (insights.length === 0 && d.analysis) {
        insights = [{ title: "AI Summary", description: d.analysis }];
      }

      setAnalysisResult({
        kpis, barData, lineData, risks, insights,
        barTitle: bar?.title, lineTitle: line?.title,
        tables: d.tables_loaded || [],
      });
      setActiveTab("overview");
    } catch (e) {
      setFileError(e.message || "Failed to analyze files.");
    }
    setAnalyzing(false);
  }

  function reset() {
    setFiles([]);
    setFileError("");
    setAnalysisResult(null);
    setDateRange({ from: "", to: "" });
  }

  // Helper: parse range for demo
  //function parseSection(start, end) {
    // stub (if used in demo for generating a CSV section)
   // return [];
  //}

  // ------------------ RENDER
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "0",
        background: COLORS.bg,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          textAlign: "center",
          paddingTop: 40,
          paddingBottom: 18,
        }}
      >
        <h1
          style={{
            fontWeight: 800,
            fontSize: 36,
            letterSpacing: "-1.5px",
            background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.purple})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: 10,
          }}
        >
          Upload Your CSV or Excel
        </h1>
        <p
          style={{
            color: COLORS.navyLight,
            fontSize: 16,
            opacity: 0.87,
            maxWidth: 520,
            margin: "0 auto",
            marginBottom: 4,
          }}
        >
          Drop one or more CSV / Excel files here — they're joined into a star schema and analyzed with real metrics & charts (<b>up to 8 files, 20MB each</b>)
        </p>
      </div>

      {/* Upload ZONE */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={handleDrop}
        style={{
          margin: "0 auto",
          marginBottom: 30,
          width: 410,
          maxWidth: "94vw",
          background: "#fff",
          border: `2px dashed ${COLORS.accent}`,
          borderRadius: 16,
          minHeight: 150,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <label
          htmlFor="fileInput"
          style={{
            width: "100%",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 36,
          }}
        >
          <div
            style={{
              fontSize: 40,
              color: COLORS.accent,
              marginBottom: 8,
            }}
          >
            📁
          </div>
          <span style={{ fontSize: 18, color: COLORS.navy, fontWeight: 600 }}>
            Drag & Drop or <span style={{ color: COLORS.accent }}>Browse</span>
          </span>
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            Only .csv, .xls, .xlsx supported
          </span>
          <input
            id="fileInput"
            type="file"
            accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            style={{ display: "none" }}
            multiple
            onChange={handleFileInput}
          />
        </label>
        {/* File display */}
        {files.length > 0 && (
          <div
            style={{
              width: "100%",
              marginTop: 8,
              borderTop: "1px solid #e5e7eb",
              paddingTop: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginBottom: 6 }}>
              {files.length} file{files.length > 1 ? "s" : ""} ready — drop more to combine into one report
            </div>
            {files.map((file, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: 14,
                  color: COLORS.navy,
                  justifyContent: "space-between",
                  background: "#f3f6fa",
                  borderRadius: 5,
                  padding: "6px 10px",
                  marginBottom: 4,
                }}
              >
                <span>
                  <b>{file.name}</b>{" "}
                  <span
                    style={{
                      opacity: 0.7,
                      fontSize: 12,
                      fontWeight: 400,
                    }}
                  >
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </span>
                <span
                  style={{
                    color: COLORS.red,
                    fontWeight: 700,
                    marginLeft: 8,
                    cursor: "pointer",
                  }}
                  onClick={() => removeFile(idx)}
                  title="Remove file"
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Error message */}
      {fileError && (
        <div
          style={{
            textAlign: "center",
            color: COLORS.red,
            marginBottom: 10,
            fontWeight: 600,
          }}
        >
          {fileError}
        </div>
      )}

      {/* DATE FILTER */}
      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          alignItems: "center",
          marginBottom: 30,
        }}
      >
        <label style={{ color: COLORS.navyLight, fontSize: 14 }}>
          Filter By Date:&nbsp;
        </label>
        <input
          type="date"
          value={dateRange.from}
          onChange={(e) =>
            setDateRange((r) => ({ ...r, from: e.target.value }))
          }
          style={{
            background: "#f9fafb",
            border: `1px solid #e5e7eb`,
            borderRadius: 5,
            fontSize: 15,
            padding: "3px 7px",
            color: COLORS.navy,
            marginRight: 8,
          }}
        />
        <span style={{ fontSize: 14, opacity: 0.7 }}>to</span>
        <input
          type="date"
          value={dateRange.to}
          onChange={(e) =>
            setDateRange((r) => ({ ...r, to: e.target.value }))
          }
          style={{
            background: "#f9fafb",
            border: `1px solid #e5e7eb`,
            borderRadius: 5,
            fontSize: 15,
            padding: "3px 7px",
            color: COLORS.navy,
            marginLeft: 8,
          }}
        />
        {isRangeTooLong(dateRange.from, dateRange.to) && (
          <span style={{ color: COLORS.warning, marginLeft: 8, fontSize: 13 }}>
            ⚠️ Range over 6 months may slow analysis
          </span>
        )}
        <button
          onClick={reset}
          style={{
            border: 0,
            background: "#fff",
            borderRadius: 5,
            color: COLORS.red,
            marginLeft: 22,
            fontWeight: 600,
            cursor: "pointer",
            fontSize: 14,
            padding: "4px 10px",
          }}
        >
          Reset
        </button>
      </div>

      {/* ANALYZE BUTTON */}
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <button
          disabled={analyzing || files.length === 0}
          onClick={handleAnalyze}
          style={{
            padding: "14px 40px",
            fontWeight: 700,
            fontSize: 17,
            background:
              files.length === 0 || analyzing
                ? "#e5e7eb"
                : `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.purple})`,
            border: 0,
            color: files.length === 0 || analyzing ? "#7d8593" : "#fff",
            borderRadius: 7,
            boxShadow:
              files.length === 0 || analyzing
                ? "none"
                : "0 2px 8px rgba(59,130,246,0.14)",
            cursor:
              files.length === 0 || analyzing ? "not-allowed" : "pointer",
            transition: "all 0.18s",
          }}
        >
          {analyzing
            ? "Analyzing..."
            : files.length > 1
            ? `Analyze ${files.length} files`
            : "Analyze"}
        </button>
      </div>

      {/* RESULTS */}
      {analysisResult && (
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 16,
            padding: 28,
            marginBottom: 80,
            boxShadow: "0 2px 16px 0 rgba(15,23,41,0.03)",
          }}
        >
          {/* TAB SELECT */}
          <div
            style={{
              display: "flex",
              gap: 14,
              borderBottom: "1px solid #e5e7eb",
              marginBottom: 24,
              paddingBottom: 3,
              fontSize: 15,
            }}
          >
            <span
              onClick={() => setActiveTab("overview")}
              style={{
                fontWeight: activeTab === "overview" ? 700 : 500,
                color:
                  activeTab === "overview"
                    ? COLORS.accent
                    : COLORS.navyLight,
                cursor: "pointer",
                borderBottom:
                  activeTab === "overview"
                    ? `2.5px solid ${COLORS.accent}`
                    : "none",
                paddingBottom: 2,
                paddingRight: 9,
              }}
            >
              Overview
            </span>
            <span
              onClick={() => setActiveTab("risks")}
              style={{
                fontWeight: activeTab === "risks" ? 700 : 500,
                color:
                  activeTab === "risks"
                    ? COLORS.red
                    : COLORS.navyLight,
                cursor: "pointer",
                borderBottom:
                  activeTab === "risks"
                    ? `2.5px solid ${COLORS.red}`
                    : "none",
                paddingBottom: 2,
                paddingRight: 9,
              }}
            >
              Data Risks
            </span>
            <span
              onClick={() => setActiveTab("insights")}
              style={{
                fontWeight: activeTab === "insights" ? 700 : 500,
                color:
                  activeTab === "insights"
                    ? COLORS.green
                    : COLORS.navyLight,
                cursor: "pointer",
                borderBottom:
                  activeTab === "insights"
                    ? `2.5px solid ${COLORS.green}`
                    : "none",
                paddingBottom: 2,
                paddingRight: 9,
              }}
            >
              AI Insights
            </span>
          </div>

          {/* TAB: OVERVIEW */}
          {activeTab === "overview" && (
            <>
              {/* KPIs */}
              {analysisResult.kpis && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 25,
                    margin: "0 0 32px 0",
                  }}
                >
                  {analysisResult.kpis.map((kpi, i) => (
                    <div
                      key={kpi.label}
                      style={{
                        background: "#f1f5fa",
                        borderRadius: 10,
                        padding: "23px 32px",
                        flex: "1 1 175px",
                        minWidth: 158,
                        boxShadow: "0 0 0 1.5px #e5e7eb",
                        fontWeight: 700,
                        color: COLORS.navy,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        fontSize: 15,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 33,
                          fontWeight: 800,
                          background: `linear-gradient(90deg, ${COLORS.kpi[i % COLORS.kpi.length]}, ${COLORS.accent})`,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          marginBottom: 6,
                        }}
                      >
                        {kpi.value.toLocaleString()}
                      </span>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: COLORS.navyLight,
                          opacity: 0.79,
                        }}
                      >
                        {kpi.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* CHARTS */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 38,
                  margin: "0 0 12px 0",
                }}
              >
                {/* Bar Chart Demo */}
                {analysisResult.barData && (
                  <div style={{ flex: "1 1 360px", minWidth: 310 }}>
                    <h4
                      style={{
                        fontWeight: 700,
                        margin: 0,
                        marginBottom: 8,
                        color: COLORS.navyLight,
                        fontSize: 15,
                      }}
                    >
                      {analysisResult.barTitle || "By Category"}
                    </h4>
                    <ResponsiveContainer width="100%" height={205}>
                      <BarChart
                        data={analysisResult.barData}
                        margin={{ top: 20, right: 18, left: 0, bottom: 8 }}
                      >
                        <XAxis
                          dataKey="name"
                          stroke="#babebf"
                          style={{ fontSize: 13, fontWeight: 600 }}
                        />
                        <YAxis
                          stroke="#babebf"
                          style={{ fontSize: 13, fontWeight: 600 }}
                        />
                        <Tooltip />
                        <Bar
                          dataKey="value"
                          fill={COLORS.accent}
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Line Chart Demo */}
                {analysisResult.lineData && (
                  <div style={{ flex: "1 1 360px", minWidth: 310 }}>
                    <h4
                      style={{
                        fontWeight: 700,
                        margin: 0,
                        marginBottom: 8,
                        color: COLORS.navyLight,
                        fontSize: 15,
                      }}
                    >
                      {analysisResult.lineTitle || "Trends Over Time"}
                    </h4>
                    <ResponsiveContainer width="100%" height={205}>
                      <LineChart
                        data={analysisResult.lineData}
                        margin={{ top: 20, right: 22, left: 0, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          stroke="#babebf"
                          style={{ fontSize: 13, fontWeight: 600 }}
                        />
                        <YAxis
                          stroke="#babebf"
                          style={{ fontSize: 13, fontWeight: 600 }}
                        />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke={COLORS.purple}
                          strokeWidth={2.5}
                          activeDot={{ r: 7 }}
                        />
                        <Legend />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          )}

          {/* TAB: RISKS */}
          {activeTab === "risks" && analysisResult.risks && (
            <div style={{ marginTop: 12 }}>
              {analysisResult.risks.length === 0 && (
                <div style={{ color: COLORS.green, fontWeight: 700 }}>
                  ✅ No data risks detected
                </div>
              )}
              {analysisResult.risks.map((risk, i) => (
                <div
                  key={i}
                  style={{
                    background: risk.severity === "high"
                      ? "#fde047"
                      : risk.severity === "medium"
                      ? "#fbbf24"
                      : "#f3f6fa",
                    color: risk.severity === "high"
                      ? "#a16207"
                      : risk.severity === "medium"
                      ? "#92400e"
                      : COLORS.navy,
                    borderRadius: 8,
                    marginBottom: 12,
                    padding: "18px 22px",
                    fontWeight: 600,
                    fontSize: 15,
                    boxShadow: "0 0 0 1.5px #ece7cb",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <span style={{ marginRight: 18, fontSize: 22 }}>
                    {risk.severity === "high"
                      ? "⚠️"
                      : risk.severity === "medium"
                      ? "🔶"
                      : "ℹ️"}
                  </span>
                  <b style={{ marginRight: 9 }}>{risk.type}:</b>
                  {risk.description}
                  <span style={{ marginLeft: "auto" }}>
                    ({risk.count || 0} cases)
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* TAB: INSIGHTS */}
          {activeTab === "insights" && analysisResult.insights && (
            <div style={{ marginTop: 12 }}>
              {analysisResult.insights.length === 0 && (
                <div style={{ color: COLORS.navyLight }}>
                  No AI insights available yet for this data.
                </div>
              )}
              {analysisResult.insights.map((insight, i) => (
                <div
                  key={i}
                  style={{
                    padding: "20px 26px",
                    background: "#f3f6fa",
                    borderRadius: 10,
                    marginBottom: 15,
                    fontWeight: 500,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 17,
                      marginBottom: 5,
                      color: COLORS.green,
                    }}
                  >
                    🧠 {insight.title}
                  </div>
                  <div style={{ color: COLORS.navyLight, fontSize: 15 }}>
                    {insight.description}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}